import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
    Users, ArrowLeft, Ruler, Coins, Beaker, Layers, Box, Settings, Binary, ShieldCheck, BarChart3, DatabaseBackup, Download, Upload, RefreshCw, AlertTriangle, HardDrive, Bot
} from 'lucide-react';
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { MasterDataCrud } from '../../components/shared/MasterDataCrud';
import { MaterialsManager } from './MaterialsManager';
import { DesignMixManager } from './DesignMixManager';
import { SupplierManager } from './SupplierManager';
import { DefaultsSettingsTab } from './DefaultsSettingsTab';
import { OverheadCostManager } from './OverheadCostManager';
import { UsersManager } from './UsersManager';
import { RolesPermissionsManager } from './RolesPermissionsManager';
import { ReportingSettingsTab } from './ReportingSettingsTab';
import { UsageAnalyticsTab } from './UsageAnalyticsTab';
import { StorageVisibilityTab } from './StorageVisibilityTab';
import { useSettings } from '../../context/SettingsContext';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import { api } from '../../services/api';
import type { DatabaseBackupStatus } from '../../services/types';

// Input Shim
function Input(props: React.InputHTMLAttributes<HTMLInputElement>) {
    return <input {...props} className={`flex-1 p-2 border rounded-md font-mono text-sm ${props.className || ''}`} />
}

export function SettingsDashboard() {
    const [searchParams, setSearchParams] = useSearchParams();
    const initialTab = searchParams.get('tab') || 'home';
    const [currentView, setCurrentView] = useState<string>(initialTab);
    const { getSetting, updateSetting, refreshSettings } = useSettings();
    const { hasPermission } = useAuth();
    const [savingSimpleMode, setSavingSimpleMode] = useState(false);
    const [savingSelectiveMode, setSavingSelectiveMode] = useState(false);
    const [savingCopilot, setSavingCopilot] = useState(false);
    const [printAsset, setPrintAsset] = useState<{
        enabled: boolean;
        has_asset: boolean;
        data_uri?: string | null;
        file_name?: string | null;
    } | null>(null);
    const [loadingPrintAsset, setLoadingPrintAsset] = useState(false);
    const [savingPrintAsset, setSavingPrintAsset] = useState(false);
    const [backupStatus, setBackupStatus] = useState<DatabaseBackupStatus | null>(null);
    const [loadingBackupStatus, setLoadingBackupStatus] = useState(false);
    const [exportingBackup, setExportingBackup] = useState(false);
    const [restoringBackup, setRestoringBackup] = useState(false);
    const [selectedRestoreFile, setSelectedRestoreFile] = useState<File | null>(null);
    const [restoreConfirm, setRestoreConfirm] = useState('');
    const [lastRestoreMessage, setLastRestoreMessage] = useState<string | null>(null);
    const printAssetInputRef = useRef<HTMLInputElement | null>(null);
    const backupFileInputRef = useRef<HTMLInputElement | null>(null);

    const simpleModeEnabled = ['1', 'true', 'yes', 'on', 'enabled'].includes(
        String(getSetting('sales_simple_mode_enabled') || '').trim().toLowerCase()
    );
    const selectiveFlowEnabled = ['1', 'true', 'yes', 'on', 'enabled'].includes(
        String(getSetting('sales_selective_flow_enabled') || '').trim().toLowerCase()
    );
    const copilotRaw = getSetting('copilot_enabled');
    const copilotEnabled = copilotRaw === '' || ['1', 'true', 'yes', 'on', 'enabled'].includes(
        String(copilotRaw).trim().toLowerCase()
    );

    const handleToggleSimpleMode = async (enabled: boolean) => {
        try {
            setSavingSimpleMode(true);
            await updateSetting('sales_simple_mode_enabled', enabled ? 'true' : 'false');
            await refreshSettings();
            toast.success(`Sales Simple Mode ${enabled ? 'enabled' : 'disabled'}`);
        } catch (error: any) {
            toast.error(error?.message || 'Failed to update Sales Simple Mode');
        } finally {
            setSavingSimpleMode(false);
        }
    };

    const handleToggleSelectiveFlow = async (enabled: boolean) => {
        try {
            setSavingSelectiveMode(true);
            await updateSetting('sales_selective_flow_enabled', enabled ? 'true' : 'false');
            await refreshSettings();
            toast.success(`Selective sale-order flow ${enabled ? 'enabled' : 'disabled'}`);
        } catch (error: any) {
            toast.error(error?.message || 'Failed to update selective sale-order flow');
        } finally {
            setSavingSelectiveMode(false);
        }
    };

    const handleToggleCopilot = async (enabled: boolean) => {
        try {
            setSavingCopilot(true);
            await updateSetting('copilot_enabled', enabled ? 'true' : 'false');
            await refreshSettings();
            toast.success(`AI Copilot ${enabled ? 'enabled' : 'disabled'}`);
        } catch (error: any) {
            toast.error(error?.message || 'Failed to update AI Copilot setting');
        } finally {
            setSavingCopilot(false);
        }
    };

    const loadPrintAsset = async () => {
        try {
            setLoadingPrintAsset(true);
            const meta = await api.settings.getPrintSignatureStamp();
            setPrintAsset(meta);
        } catch (error: any) {
            console.error('Failed to load print signature/stamp metadata', error);
            setPrintAsset(null);
        } finally {
            setLoadingPrintAsset(false);
        }
    };

    const handleUploadPrintAsset = async (file?: File | null) => {
        if (!file) return;
        try {
            setSavingPrintAsset(true);
            const meta = await api.settings.uploadPrintSignatureStamp(file, true);
            setPrintAsset(meta);
            await refreshSettings();
            toast.success('Print signature/stamp uploaded');
        } catch (error: any) {
            toast.error(error?.message || 'Failed to upload print signature/stamp');
        } finally {
            setSavingPrintAsset(false);
            if (printAssetInputRef.current) {
                printAssetInputRef.current.value = '';
            }
        }
    };

    const handleRemovePrintAsset = async () => {
        try {
            setSavingPrintAsset(true);
            const meta = await api.settings.removePrintSignatureStamp();
            setPrintAsset(meta);
            await refreshSettings();
            toast.success('Print signature/stamp removed');
        } catch (error: any) {
            toast.error(error?.message || 'Failed to remove print signature/stamp');
        } finally {
            setSavingPrintAsset(false);
        }
    };

    const handleTogglePrintAssetEnabled = async (enabled: boolean) => {
        try {
            setSavingPrintAsset(true);
            await updateSetting('print_signature_stamp_enabled', enabled ? 'true' : 'false');
            await refreshSettings();
            await loadPrintAsset();
            toast.success(`Print signature/stamp ${enabled ? 'enabled' : 'disabled'}`);
        } catch (error: any) {
            toast.error(error?.message || 'Failed to update print signature/stamp setting');
        } finally {
            setSavingPrintAsset(false);
        }
    };

    const formatBytes = (bytes?: number | null) => {
        const value = Number(bytes || 0);
        if (value <= 0) return '0 B';
        const units = ['B', 'KB', 'MB', 'GB'];
        let size = value;
        let unitIndex = 0;
        while (size >= 1024 && unitIndex < units.length - 1) {
            size /= 1024;
            unitIndex += 1;
        }
        return `${size.toFixed(size >= 10 || unitIndex === 0 ? 0 : 1)} ${units[unitIndex]}`;
    };

    const formatServerTime = (value?: string | null) => {
        if (!value) return 'Not available';
        const date = new Date(value);
        if (Number.isNaN(date.getTime())) return value;
        return date.toLocaleString();
    };

    const loadDatabaseBackupStatus = async () => {
        try {
            setLoadingBackupStatus(true);
            const status = await api.settings.getDatabaseBackupStatus();
            setBackupStatus(status);
        } catch (error: any) {
            console.error('Failed to load database backup status', error);
            setBackupStatus(null);
        } finally {
            setLoadingBackupStatus(false);
        }
    };

    const handleExportDatabaseBackup = async () => {
        try {
            setExportingBackup(true);
            await api.settings.exportDatabaseBackup();
            toast.success('Database backup exported');
            await loadDatabaseBackupStatus();
        } catch (error: any) {
            toast.error(error?.message || 'Failed to export database backup');
        } finally {
            setExportingBackup(false);
        }
    };

    const handleRestoreDatabaseBackup = async () => {
        if (!selectedRestoreFile) {
            toast.error('Choose a backup file first');
            return;
        }
        if (restoreConfirm.trim() !== 'RESTORE Hadaf') {
            toast.error('Type RESTORE Hadaf to confirm restore');
            return;
        }
        try {
            setRestoringBackup(true);
            const result = await api.settings.restoreDatabaseBackup(selectedRestoreFile);
            setLastRestoreMessage(result.message);
            setSelectedRestoreFile(null);
            setRestoreConfirm('');
            if (backupFileInputRef.current) {
                backupFileInputRef.current.value = '';
            }
            toast.success('Database backup restored');
            await loadDatabaseBackupStatus();
        } catch (error: any) {
            toast.error(error?.message || 'Failed to restore database backup');
        } finally {
            setRestoringBackup(false);
        }
    };

    const modules: Array<{
        id: string;
        label: string;
        icon: any;
        section: string;
        type: string;
        table?: string;
        pk?: string;
        columns?: { key: string; label: string; type?: 'text' | 'number' }[];
    }> = [
            // Management Section
            { id: 'suppliers', label: 'Suppliers', icon: Users, section: 'Management', type: 'custom' },
            { id: 'overhead', label: 'Overhead Cost', icon: Coins, section: 'Management', type: 'custom' },
            { id: 'units', label: 'Units', icon: Ruler, section: 'Management', type: 'crud', table: 'tbl_units', pk: 'unit_id', columns: [{ key: 'unit', label: 'Unit Name' }] },
            { id: 'defaults', label: 'Default Settings', icon: Settings, section: 'Management', type: 'custom' },
            { id: 'reporting', label: 'Reporting Setup', icon: BarChart3, section: 'Management', type: 'custom' },
            { id: 'analytics', label: 'Usage Analytics', icon: BarChart3, section: 'Management', type: 'custom' },
            { id: 'storage', label: 'Storage & Sharing', icon: HardDrive, section: 'Management', type: 'custom' },
            { id: 'users', label: 'Users', icon: Users, section: 'Management', type: 'custom' },
            { id: 'roles', label: 'Roles & Permissions', icon: ShieldCheck, section: 'Management', type: 'custom' },

            // Design Section
            { id: 'mat_categories', label: 'Material Categories', icon: Layers, section: 'Design', type: 'crud', table: 'tbl_material_categories', pk: 'material_category_id', columns: [{ key: 'material_category', label: 'Category Name' }] },
            { id: 'materials', label: 'Materials', icon: Box, section: 'Design', type: 'custom' },
            { id: 'design_mix', label: 'Design Mix Models', icon: Beaker, section: 'Design', type: 'custom' },
            { id: 'mould_type', label: 'Mould Type', icon: Binary, section: 'Design', type: 'crud', table: 'tbl_mould_types', pk: 'mould_type_id', columns: [{ key: 'mould_type', label: 'Mould Type' }, { key: 'mould_cost', label: 'Cost', type: 'number' }] },


        ];

    const visibleModules = modules.filter((module) => (
        module.id !== 'analytics' || hasPermission('system.settings.manage')
    ));
    const activeModule = visibleModules.find(m => m.id === currentView);

    const renderContent = () => {
        if (currentView === 'home') {
            return (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    {/* Management Column */}
                    <div className="space-y-4">
                        <h2 className="text-xl font-bold border-b pb-2 text-slate-700">Management</h2>
                        <Card>
                            <CardContent className="py-4 space-y-2">
                                <div className="text-sm font-semibold text-slate-700">Sales Flow Defaults</div>
                                <label className="flex items-center gap-3 text-sm">
                                    <input
                                        type="checkbox"
                                        className="h-4 w-4"
                                        checked={simpleModeEnabled}
                                        disabled={savingSimpleMode}
                                        onChange={(event) => handleToggleSimpleMode(event.target.checked)}
                                    />
                                    <span>
                                        Use simplified flow as the global default/fallback ({'Inquiry -> Estimate -> SO -> DN -> Invoice'}) with provisional stock on SO confirm.
                                    </span>
                                </label>
                                <label className="flex items-center gap-3 text-sm">
                                    <input
                                        type="checkbox"
                                        className="h-4 w-4"
                                        checked={selectiveFlowEnabled}
                                        disabled={savingSelectiveMode}
                                        onChange={(event) => handleToggleSelectiveFlow(event.target.checked)}
                                    />
                                    <span>
                                        Allow flow selection per sale order. When disabled, the global default above controls all orders.
                                    </span>
                                </label>
                                <p className="text-xs text-slate-500">
                                    Keep selective flow off for a no-change production rollout. Turn it on only when you want draft sale orders to choose between `Simple` and `Production`.
                                </p>
                            </CardContent>
                        </Card>
                        <Card>
                            <CardContent className="py-4 space-y-2">
                                <div className="flex items-center gap-2 text-sm font-semibold text-slate-700">
                                    <Bot className="h-4 w-4" />
                                    AI Copilot
                                </div>
                                <label className="flex items-center gap-3 text-sm">
                                    <input
                                        type="checkbox"
                                        className="h-4 w-4"
                                        checked={copilotEnabled}
                                        disabled={savingCopilot}
                                        onChange={(event) => handleToggleCopilot(event.target.checked)}
                                    />
                                    <span>
                                        Enable AI Copilot assistant for all users
                                    </span>
                                </label>
                                <p className="text-xs text-slate-500">
                                    When disabled, the AI Copilot chat button and panel are hidden across the entire application.
                                </p>
                            </CardContent>
                        </Card>
                        <Card>
                            <CardContent className="py-4 space-y-3">
                                <div className="text-sm font-semibold text-slate-700">Print Signature &amp; Stamp</div>
                                <p className="text-xs text-slate-500">
                                    Global signature image used on Invoice and Delivery Note prints.
                                </p>
                                {loadingPrintAsset ? (
                                    <div className="text-xs text-slate-500">Loading print asset...</div>
                                ) : (
                                    <>
                                        <label className="flex items-center gap-3 text-sm">
                                            <input
                                                type="checkbox"
                                                className="h-4 w-4"
                                                checked={Boolean(printAsset?.enabled)}
                                                disabled={savingPrintAsset || !(printAsset?.has_asset)}
                                                onChange={(event) => handleTogglePrintAssetEnabled(event.target.checked)}
                                            />
                                            <span>Enable signature image in prints</span>
                                        </label>
                                        {printAsset?.has_asset && printAsset?.data_uri ? (
                                            <div className="rounded border border-slate-200 bg-slate-50 p-2">
                                                <img
                                                    src={printAsset.data_uri}
                                                    alt="Signature and stamp preview"
                                                    className="max-h-20 object-contain"
                                                />
                                                <div className="mt-1 text-[11px] text-slate-500">{printAsset.file_name || 'Uploaded image'}</div>
                                            </div>
                                        ) : (
                                            <div className="text-xs text-slate-500">No signature/stamp image uploaded.</div>
                                        )}
                                        <div className="flex flex-wrap gap-2">
                                            <Button
                                                type="button"
                                                variant="outline"
                                                size="sm"
                                                disabled={savingPrintAsset}
                                                onClick={() => printAssetInputRef.current?.click()}
                                            >
                                                {printAsset?.has_asset ? 'Replace Image' : 'Upload Image'}
                                            </Button>
                                            <Button
                                                type="button"
                                                variant="outline"
                                                size="sm"
                                                disabled={savingPrintAsset || !printAsset?.has_asset}
                                                onClick={handleRemovePrintAsset}
                                            >
                                                Remove
                                            </Button>
                                        </div>
                                        <input
                                            ref={printAssetInputRef}
                                            type="file"
                                            accept="image/png,image/jpeg,image/webp"
                                            className="hidden"
                                            onChange={(event) => handleUploadPrintAsset(event.target.files?.[0] || null)}
                                        />
                                    </>
                                )}
                            </CardContent>
                        </Card>
                        <Card>
                            <CardContent className="py-4 space-y-3">
                                <div className="flex items-center justify-between gap-3">
                                    <div className="flex items-center gap-2 text-sm font-semibold text-slate-700">
                                        <DatabaseBackup className="h-4 w-4" />
                                        Database Backup
                                    </div>
                                    <Button
                                        type="button"
                                        variant="ghost"
                                        size="sm"
                                        disabled={loadingBackupStatus}
                                        onClick={loadDatabaseBackupStatus}
                                    >
                                        <RefreshCw className={`h-4 w-4 ${loadingBackupStatus ? 'animate-spin' : ''}`} />
                                    </Button>
                                </div>
                                <div className="grid grid-cols-2 gap-3 text-xs">
                                    <div>
                                        <div className="text-slate-500">Database</div>
                                        <div className="font-medium text-slate-800">{backupStatus?.db_type || 'Loading...'}</div>
                                    </div>
                                    <div>
                                        <div className="text-slate-500">Size</div>
                                        <div className="font-medium text-slate-800">{formatBytes(backupStatus?.size_bytes)}</div>
                                    </div>
                                    <div>
                                        <div className="text-slate-500">Status Time</div>
                                        <div className="font-medium text-slate-800">{formatServerTime(backupStatus?.server_time_utc)}</div>
                                    </div>
                                    <div>
                                        <div className="text-slate-500">Restore</div>
                                        <div className={`font-medium ${backupStatus?.restore_supported ? 'text-emerald-700' : 'text-red-700'}`}>
                                            {backupStatus?.restore_supported ? 'Available' : 'Unavailable'}
                                        </div>
                                    </div>
                                </div>
                                <div className="flex items-start gap-2 rounded border border-amber-200 bg-amber-50 p-2 text-xs text-amber-800">
                                    <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                                    <span>Restore starts a short maintenance window. Run it outside working hours; users should refresh or sign in again after completion. This database backup does not include uploaded document or signature image files.</span>
                                </div>
                                {lastRestoreMessage && (
                                    <div className="rounded border border-emerald-200 bg-emerald-50 p-2 text-xs text-emerald-800">
                                        {lastRestoreMessage}
                                    </div>
                                )}
                                <div className="flex flex-wrap gap-2">
                                    <Button
                                        type="button"
                                        variant="outline"
                                        size="sm"
                                        disabled={exportingBackup || !backupStatus?.supported}
                                        onClick={handleExportDatabaseBackup}
                                    >
                                        <Download className="h-4 w-4 mr-2" />
                                        {exportingBackup ? 'Exporting...' : 'Export Backup'}
                                    </Button>
                                    <Button
                                        type="button"
                                        variant="outline"
                                        size="sm"
                                        disabled={restoringBackup || !backupStatus?.restore_supported}
                                        onClick={() => backupFileInputRef.current?.click()}
                                    >
                                        <Upload className="h-4 w-4 mr-2" />
                                        Choose Restore File
                                    </Button>
                                </div>
                                <input
                                    ref={backupFileInputRef}
                                    type="file"
                                    accept=".zip,application/zip"
                                    className="hidden"
                                    onChange={(event) => {
                                        setSelectedRestoreFile(event.target.files?.[0] || null);
                                        setLastRestoreMessage(null);
                                    }}
                                />
                                {selectedRestoreFile && (
                                    <div className="space-y-2 rounded border border-slate-200 p-3">
                                        <div className="text-xs text-slate-600">
                                            Selected: <span className="font-medium text-slate-800">{selectedRestoreFile.name}</span>
                                        </div>
                                        <Input
                                            value={restoreConfirm}
                                            onChange={(event) => setRestoreConfirm(event.target.value)}
                                            placeholder="Type RESTORE Hadaf"
                                            className="w-full flex-none font-sans"
                                        />
                                        <Button
                                            type="button"
                                            size="sm"
                                            disabled={restoringBackup || restoreConfirm.trim() !== 'RESTORE Hadaf'}
                                            onClick={handleRestoreDatabaseBackup}
                                            className="bg-red-600 hover:bg-red-700"
                                        >
                                            {restoringBackup ? 'Restoring...' : 'Restore Backup'}
                                        </Button>
                                    </div>
                                )}
                            </CardContent>
                        </Card>
                        <div className="grid gap-3">
                            {visibleModules.filter(m => m.section === 'Management').map(m => (
                                <Button
                                    key={m.id}
                                    variant="outline"
                                    className="h-14 justify-start text-lg px-6 hover:bg-blue-50 hover:text-blue-700 border-slate-300"
                                    onClick={() => setCurrentView(m.id)}
                                >
                                    <m.icon className="w-6 h-6 mr-3" />
                                    {m.label}
                                </Button>
                            ))}
                        </div>
                    </div>

                    {/* Design Column */}
                    <div className="space-y-4">
                        <h2 className="text-xl font-bold border-b pb-2 text-slate-700">Design</h2>
                        <div className="grid gap-3">
                            {visibleModules.filter(m => m.section === 'Design').map(m => (
                                <Button
                                    key={m.id}
                                    variant="outline"
                                    className="h-14 justify-start text-lg px-6 hover:bg-emerald-50 hover:text-emerald-700 border-slate-300"
                                    onClick={() => setCurrentView(m.id)}
                                >
                                    <m.icon className="w-6 h-6 mr-3" />
                                    {m.label}
                                </Button>
                            ))}
                        </div>


                    </div>
                </div>
            );
        }


        if (currentView === 'defaults') return <DefaultsSettingsTab />;
        if (currentView === 'materials') return <MaterialsManager />;
        if (currentView === 'design_mix') return <DesignMixManager />;
        if (currentView === 'suppliers') return <SupplierManager />;
        if (currentView === 'overhead') return <OverheadCostManager />;
        if (currentView === 'reporting') return <ReportingSettingsTab />;
        if (currentView === 'analytics' && hasPermission('system.settings.manage')) return <UsageAnalyticsTab />;
        if (currentView === 'storage') return <StorageVisibilityTab />;
        if (currentView === 'users') return <UsersManager />;
        if (currentView === 'roles') return <RolesPermissionsManager />;

        if (activeModule?.type === 'crud') {
            return (
                <MasterDataCrud
                    title={activeModule.label}
                    tableName={activeModule.table!}
                    pkField={activeModule.pk!}
                    columns={activeModule.columns!}
                />
            );
        }

        return (
            <Card>
                <CardContent className="py-12 text-center text-slate-400">
                    <p>Module "{activeModule?.label}" is under construction.</p>
                </CardContent>
            </Card>
        );
    };

    useEffect(() => {
        if (currentView !== 'home' && !modules.find(m => m.id === currentView)) {
            setCurrentView('home');
        }
    }, [currentView]);

    useEffect(() => {
        loadPrintAsset();
        loadDatabaseBackupStatus();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <div className="p-6 space-y-6 h-full flex flex-col">
            <div className="flex items-center gap-4 shrink-0">
                {currentView !== 'home' && (
                    <Button variant="ghost" size="icon" onClick={() => setCurrentView('home')}>
                        <ArrowLeft className="w-6 h-6" />
                    </Button>
                )}
                <div>
                    <h1 className="text-3xl font-bold tracking-tight text-foreground">
                        {currentView === 'home' ? 'System Settings' : activeModule?.label}
                    </h1>
                    {currentView === 'home' && (
                        <p className="text-muted-foreground mt-1">Manage definitions, masters, and system configuration.</p>
                    )}
                </div>
            </div>

            <div className="flex-1 min-h-0">
                {renderContent()}
            </div>
        </div>
    );
}
